import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getDraftParts, updateDraftPart, approveDraftPart, discardDraftPart,
  getCategories, getStores, getLocations, getWorkOrders,
} from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Boxes, CheckCircle2, Trash2, Settings, AlertTriangle, Package, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const DraftInventory = () => {
  const [drafts, setDrafts] = useState([]);
  const [cats, setCats] = useState([]);
  const [stores, setStores] = useState([]);
  const [locationsByStore, setLocationsByStore] = useState({}); // storeId -> locations[]
  const [workOrders, setWorkOrders] = useState([]); // for the attach-to-WO picker
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [approvingDraft, setApprovingDraft] = useState(null);

  const refresh = () => Promise.all([
    getDraftParts(), getCategories(), getStores(), getWorkOrders(),
  ])
    .then(async ([d, c, s, w]) => {
      setDrafts(d); setCats(c); setStores(s); setWorkOrders(w || []);
      // Pre-fetch all store locations so the approval dialog never waits on
      // the network when the user picks a store. ~5 stores max in practice.
      const locMap = {};
      await Promise.all(s.map(async st => {
        try { locMap[st.id] = await getLocations(st.id); }
        catch { locMap[st.id] = []; }
      }));
      setLocationsByStore(locMap);
    });

  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const updateField = async (id, patch) => {
    setBusyId(id);
    try {
      const updated = await updateDraftPart(id, patch);
      setDrafts(drafts.map(d => d.id === id ? { ...d, ...updated } : d));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const approve = async (d) => {
    if (!d.categoryId) { toast.error('Pick a category first'); return; }
    if (!d.sellPrice) { toast.error('Sell price missing — pick a category with a markup or set it manually.'); return; }
    // Open the store-picker dialog. The dialog enforces storeId+locationId
    // so opening stock gets seeded — fixes the "approved part shows qty 0"
    // bug the user hit on the Toolstation bill.
    setApprovingDraft(d);
  };

  const finishApprove = async (d, payload) => {
    setBusyId(d.id);
    try {
      await approveDraftPart(d.id, payload);
      const qty = Math.round(Number(d.sourceBillQuantity) || 0);
      if (payload.mode === 'attach-wo') {
        const wo = workOrders.find(w => w.id === payload.workOrderId);
        const woLabel = wo ? `${wo.id} · ${wo.title || 'Untitled'}` : payload.workOrderId;
        const billedNote = payload.alreadyBilled
          ? 'not added to draft invoice (already billed)'
          : 'will be added to the next draft invoice';
        toast.success(`${d.name} approved · attached to ${woLabel} — ${billedNote}`);
      } else {
        toast.success(`${d.name} approved · stocked ${qty} unit${qty === 1 ? '' : 's'}`);
      }
      setApprovingDraft(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approval failed');
    } finally {
      setBusyId(null);
    }
  };

  const discard = async (d) => {
    if (!window.confirm(`Discard draft "${d.name}"? This cannot be undone.`)) return;
    setBusyId(d.id);
    try {
      await discardDraftPart(d.id);
      toast.success('Discarded');
      await refresh();
    } catch (e) {
      toast.error('Failed');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div className="p-6 text-slate-500">Loading...</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="draft-inventory-page">
      <PageHeader
        title="Draft Inventory"
        description="Items captured from supplier bills (lines posted to Cost-of-Sales accounts). Pick a category for each — its markup % auto-fills the sell price — then approve to push to both Xero and your portal."
        actions={
          <Link to="/admin/inventory/settings" className="text-xs flex items-center gap-1 text-slate-600 hover:text-slate-900 underline">
            <Settings className="h-3.5 w-3.5" /> Manage categories
          </Link>
        }
      />

      {cats.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-4 py-3 text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> No categories yet. Create one in <Link to="/admin/inventory/settings" className="underline mx-1">Inventory Settings</Link> first so draft parts can pick up a markup.
        </div>
      )}

      {drafts.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
          <Boxes className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <div className="font-display uppercase tracking-wide text-slate-700 mb-1">Nothing in the queue</div>
          <div className="text-sm text-slate-500">
            Draft inventory items appear here when you push a bill to Xero with lines under "Cost of Sales" / DIRECTCOSTS accounts.
          </div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          {/* Desktop table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-3 py-3">SKU</th>
                  <th className="text-left px-3 py-3 min-w-[280px]">Name</th>
                  <th className="text-right px-3 py-3">Cost £</th>
                  <th className="text-left px-3 py-3 min-w-[200px]">Category</th>
                  <th className="text-right px-3 py-3">Sell £</th>
                  <th className="text-left px-3 py-3">From bill</th>
                  <th className="text-right px-3 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {drafts.map(d => (
                  <tr key={d.id} data-testid={`draft-row-${d.id}`}>
                    <td className="px-3 py-2">
                      <Input value={d.sku || ''} className="h-8 text-xs font-mono" disabled={busyId === d.id}
                        onBlur={(e) => e.target.value !== d.sku && updateField(d.id, { sku: e.target.value })}
                        onChange={(e) => setDrafts(drafts.map(x => x.id === d.id ? { ...x, sku: e.target.value } : x))} />
                    </td>
                    <td className="px-3 py-2">
                      <Input value={d.name || ''} className="h-8" disabled={busyId === d.id}
                        onBlur={(e) => e.target.value !== d.name && updateField(d.id, { name: e.target.value })}
                        onChange={(e) => setDrafts(drafts.map(x => x.id === d.id ? { ...x, name: e.target.value } : x))} />
                    </td>
                    <td className="px-3 py-2 w-24">
                      <Input type="number" step="0.01" value={d.unitCost ?? 0} className="h-8 text-right" disabled={busyId === d.id}
                        onBlur={(e) => parseFloat(e.target.value) !== d.unitCost && updateField(d.id, { unitCost: parseFloat(e.target.value) || 0 })}
                        onChange={(e) => setDrafts(drafts.map(x => x.id === d.id ? { ...x, unitCost: parseFloat(e.target.value) || 0 } : x))} />
                    </td>
                    <td className="px-3 py-2">
                      <Select
                        value={d.categoryId || ''}
                        onValueChange={(cid) => updateField(d.id, { categoryId: cid })}
                        disabled={busyId === d.id || cats.length === 0}
                      >
                        <SelectTrigger className="h-8 text-sm" data-testid={`category-select-${d.id}`}>
                          <SelectValue placeholder={cats.length ? 'Pick a category…' : 'No categories'} />
                        </SelectTrigger>
                        <SelectContent>
                          {cats.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name} <span className="text-slate-400 text-xs ml-1">({c.markupPct}%)</span></SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 w-28">
                      <Input type="number" step="0.01" value={d.sellPrice ?? ''}
                        placeholder={d.suggestedSellPrice ? `£${d.suggestedSellPrice}` : '—'}
                        className="h-8 text-right" disabled={busyId === d.id}
                        onBlur={(e) => parseFloat(e.target.value || 0) !== d.sellPrice && updateField(d.id, { sellPrice: parseFloat(e.target.value) || 0 })}
                        onChange={(e) => setDrafts(drafts.map(x => x.id === d.id ? { ...x, sellPrice: e.target.value === '' ? null : parseFloat(e.target.value) || 0 } : x))} />
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      <div className="truncate max-w-[180px]">{d.supplier}</div>
                      {d.sourceBillQuantity > 0 && (
                        <div className="text-slate-700"><span className="font-semibold">+{Math.round(d.sourceBillQuantity)}</span> on bill</div>
                      )}
                      {d.sourceBillId && (
                        <Link to={`/admin/bills/${d.sourceBillId}`} className="text-blue-600 hover:underline">View bill</Link>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" onClick={() => approve(d)}
                        disabled={busyId === d.id || !d.categoryId || !d.sellPrice}
                        className="bg-green-700 hover:bg-green-800 text-white"
                        data-testid={`approve-draft-${d.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => discard(d)}
                        disabled={busyId === d.id}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`discard-draft-${d.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden divide-y divide-slate-100">
            {drafts.map(d => (
              <div key={d.id} className="p-4 space-y-3" data-testid={`draft-card-${d.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">SKU {d.sku}</div>
                    <Input value={d.name || ''} className="mt-1 h-9" disabled={busyId === d.id}
                      onBlur={(e) => e.target.value !== d.name && updateField(d.id, { name: e.target.value })}
                      onChange={(e) => setDrafts(drafts.map(x => x.id === d.id ? { ...x, name: e.target.value } : x))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Cost £</Label>
                    <Input type="number" step="0.01" value={d.unitCost ?? 0} className="mt-1 h-9 text-right" disabled={busyId === d.id}
                      onBlur={(e) => parseFloat(e.target.value) !== d.unitCost && updateField(d.id, { unitCost: parseFloat(e.target.value) || 0 })}
                      onChange={(e) => setDrafts(drafts.map(x => x.id === d.id ? { ...x, unitCost: parseFloat(e.target.value) || 0 } : x))} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Sell £</Label>
                    <Input type="number" step="0.01" value={d.sellPrice ?? ''}
                      placeholder={d.suggestedSellPrice ? `£${d.suggestedSellPrice}` : '—'}
                      className="mt-1 h-9 text-right" disabled={busyId === d.id}
                      onBlur={(e) => parseFloat(e.target.value || 0) !== d.sellPrice && updateField(d.id, { sellPrice: parseFloat(e.target.value) || 0 })}
                      onChange={(e) => setDrafts(drafts.map(x => x.id === d.id ? { ...x, sellPrice: e.target.value === '' ? null : parseFloat(e.target.value) || 0 } : x))} />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Category</Label>
                  <Select
                    value={d.categoryId || ''}
                    onValueChange={(cid) => updateField(d.id, { categoryId: cid })}
                    disabled={busyId === d.id || cats.length === 0}
                  >
                    <SelectTrigger className="mt-1 h-9 text-sm">
                      <SelectValue placeholder={cats.length ? 'Pick a category…' : 'No categories'} />
                    </SelectTrigger>
                    <SelectContent>
                      {cats.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name} <span className="text-slate-400 text-xs ml-1">({c.markupPct}%)</span></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <div className="text-xs text-slate-500 truncate flex-1 mr-2">
                    {d.supplier}
                    {d.sourceBillId && (
                      <> · <Link to={`/admin/bills/${d.sourceBillId}`} className="text-blue-600 hover:underline">View bill</Link></>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" onClick={() => approve(d)}
                      disabled={busyId === d.id || !d.categoryId || !d.sellPrice}
                      className="bg-green-700 hover:bg-green-800 text-white">
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => discard(d)}
                      disabled={busyId === d.id}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approval dialog — picks either a store+location to stock the
          part, OR a work order to attach it directly to (bypassing
          stock). Fixes the case where a bill line was really a one-off
          consumable for a specific job and shouldn't clutter inventory. */}
      <ApproveDraftDialog
        draft={approvingDraft}
        stores={stores}
        locationsByStore={locationsByStore}
        workOrders={workOrders}
        onCancel={() => setApprovingDraft(null)}
        onConfirm={finishApprove}
        busy={!!busyId}
      />
    </div>
  );
};

const ApproveDraftDialog = ({ draft, stores, locationsByStore, workOrders, onCancel, onConfirm, busy }) => {
  const [mode, setMode] = useState('stock');
  const [storeId, setStoreId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [workOrderId, setWorkOrderId] = useState('');
  const [woQuery, setWoQuery] = useState('');
  const [alreadyBilled, setAlreadyBilled] = useState(false);

  useEffect(() => {
    if (!draft) return;
    setMode('stock');
    setWorkOrderId('');
    setWoQuery('');
    setAlreadyBilled(false);
    // Default to the first store + its first location so the most-common
    // case is one tap (admins only need to override for multi-site work).
    const first = stores[0];
    setStoreId(first?.id || '');
    setLocationId((locationsByStore[first?.id] || [])[0]?.id || '');
  }, [draft, stores, locationsByStore]);

  // Filter work-orders live by ID / title / site — completed and
  // unscheduled WOs are intentionally included because the admin
  // needs to attach past bill lines to jobs that are already closed.
  // Also normalise the customer name — some legacy WOs carry `customer`
  // (string) while newer ones use `customerName`. Fall through both.
  const filteredWos = useMemo(() => {
    const custName = (w) => w.customerName || w.customer || '';
    const q = (woQuery || '').trim().toLowerCase();
    const all = (workOrders || []).slice().sort((a, b) => {
      // Non-completed first (surface actionable jobs), then by id desc.
      const ac = a.status === 'completed' ? 1 : 0;
      const bc = b.status === 'completed' ? 1 : 0;
      if (ac !== bc) return ac - bc;
      return (b.id || '').localeCompare(a.id || '');
    });
    if (!q) return all.slice(0, 60);
    return all.filter(w =>
      (w.id || '').toLowerCase().includes(q)
      || (w.title || '').toLowerCase().includes(q)
      || custName(w).toLowerCase().includes(q)
      || (w.siteName || '').toLowerCase().includes(q),
    ).slice(0, 60);
  }, [workOrders, woQuery]);

  if (!draft) return null;
  const qty = Math.round(Number(draft.sourceBillQuantity) || 0);
  const locs = locationsByStore[storeId] || [];
  const selectedWo = workOrders?.find(w => w.id === workOrderId);

  const canConfirm = !busy && (
    (mode === 'stock' && !!storeId && !!locationId)
    || (mode === 'attach-wo' && !!workOrderId)
  );

  const handleConfirm = () => {
    if (mode === 'stock') {
      onConfirm(draft, { mode: 'stock', storeId, locationId });
    } else {
      onConfirm(draft, { mode: 'attach-wo', workOrderId, alreadyBilled });
    }
  };

  return (
    <Dialog open={!!draft} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent
        className="max-w-lg w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        data-testid="approve-draft-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl pr-6">Approve &ldquo;{draft.name}&rdquo;</DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Choose whether this part should go into stock or be attached directly to a work order.
          </DialogDescription>
        </DialogHeader>

        {/* Draft summary card */}
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm flex items-center gap-3">
          <Package className="h-4 w-4 text-slate-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{draft.name}</div>
            <div className="text-xs text-slate-500 truncate">
              SKU {draft.sku} · cost £{Number(draft.unitCost || 0).toFixed(2)} · sell £{Number(draft.sellPrice || 0).toFixed(2)}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">From bill</div>
            <div className="font-semibold tabular-nums">+{qty}</div>
          </div>
        </div>

        {/* Mode selector — the two rectangles the admin taps to pick the flow. */}
        <div className="grid grid-cols-2 gap-2 pt-2">
          <button
            type="button"
            onClick={() => setMode('stock')}
            data-testid="approve-mode-stock"
            className={`text-left rounded-md border-2 p-3 transition-colors ${
              mode === 'stock'
                ? 'border-green-600 bg-green-50'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <Boxes className={`h-4 w-4 ${mode === 'stock' ? 'text-green-700' : 'text-slate-500'}`} />
              <span className="text-sm font-semibold">Stock it</span>
            </div>
            <div className="text-[11px] text-slate-600 leading-snug">
              Add {qty} unit{qty === 1 ? '' : 's'} into inventory at a store/location.
            </div>
          </button>
          <button
            type="button"
            onClick={() => setMode('attach-wo')}
            data-testid="approve-mode-attach-wo"
            className={`text-left rounded-md border-2 p-3 transition-colors ${
              mode === 'attach-wo'
                ? 'border-blue-600 bg-blue-50'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <Wrench className={`h-4 w-4 ${mode === 'attach-wo' ? 'text-blue-700' : 'text-slate-500'}`} />
              <span className="text-sm font-semibold">Attach to work order</span>
            </div>
            <div className="text-[11px] text-slate-600 leading-snug">
              Skip stock — bill straight through a specific job.
            </div>
          </button>
        </div>

        {/* Mode-specific form */}
        {mode === 'stock' ? (
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Store</Label>
              <Select value={storeId} onValueChange={(v) => { setStoreId(v); setLocationId((locationsByStore[v] || [])[0]?.id || ''); }}>
                <SelectTrigger className="mt-1" data-testid="approve-store-select">
                  <SelectValue placeholder="Pick a store…" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Location</Label>
              <Select value={locationId} onValueChange={setLocationId} disabled={!storeId || locs.length === 0}>
                <SelectTrigger className="mt-1" data-testid="approve-location-select">
                  <SelectValue placeholder={locs.length ? 'Pick a location…' : 'No locations in this store'} />
                </SelectTrigger>
                <SelectContent>
                  {locs.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Work order</Label>
              <Input
                placeholder="Search by ID, title, customer or site…"
                value={woQuery}
                onChange={(e) => setWoQuery(e.target.value)}
                className="mt-1 h-9"
                data-testid="approve-wo-search"
              />
              <div className="mt-2 border border-slate-200 rounded-md divide-y divide-slate-100 max-h-56 overflow-y-auto">
                {filteredWos.length === 0 ? (
                  <div className="p-3 text-xs text-slate-500 text-center">No matching work orders.</div>
                ) : filteredWos.map(w => {
                  const active = workOrderId === w.id;
                  const status = (w.status || 'open').toLowerCase();
                  const isDone = status === 'completed';
                  const isUnsched = status === 'unscheduled';
                  const isCancelled = status === 'cancelled';
                  const custName = w.customerName || w.customer || '';
                  const statusLabel = status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  return (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => setWorkOrderId(w.id)}
                      data-testid={`approve-wo-option-${w.id}`}
                      className={`w-full text-left p-2.5 transition-colors ${
                        active ? 'bg-blue-50 border-l-4 border-blue-600' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] font-mono text-slate-500">{w.id}</div>
                          <div className="text-sm font-medium truncate">{w.title || 'Untitled'}</div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {custName || 'No customer'}
                            {w.siteName ? ` · ${w.siteName}` : ''}
                          </div>
                        </div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${
                          isDone
                            ? 'bg-slate-100 text-slate-600'
                            : isCancelled
                              ? 'bg-red-100 text-red-700'
                              : isUnsched
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-emerald-100 text-emerald-800'
                        }`}>
                          {statusLabel}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
              {selectedWo && (
                <div className="mt-2 text-[11px] text-blue-800 bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
                  Attaching <span className="font-mono">{qty}× {draft.name}</span> to <span className="font-mono">{selectedWo.id}</span>.
                </div>
              )}
            </div>

            {/* Billed-already toggle. Same UX for every WO status. */}
            <label
              className="flex items-start gap-2 rounded-md border border-slate-200 bg-white p-2.5 cursor-pointer hover:bg-slate-50"
              data-testid="approve-already-billed-row"
            >
              <Checkbox
                checked={alreadyBilled}
                onCheckedChange={(v) => setAlreadyBilled(!!v)}
                className="mt-0.5"
                data-testid="approve-already-billed-checkbox"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">This part has already been billed to the customer</div>
                <div className="text-[11px] text-slate-600 mt-0.5">
                  {alreadyBilled
                    ? 'Will NOT be added to a draft invoice — recorded on the work order for tracking only.'
                    : 'Will be added to the customer\'s next draft invoice via the standard clock-out accumulator.'}
                </div>
              </div>
            </label>
          </div>
        )}

        <DialogFooter className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:justify-end">
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="approve-confirm-btn"
            className={mode === 'stock'
              ? 'bg-green-700 hover:bg-green-800 text-white'
              : 'bg-blue-700 hover:bg-blue-800 text-white'}
          >
            <CheckCircle2 className="h-4 w-4 mr-1.5" />
            {busy
              ? 'Approving…'
              : (mode === 'stock'
                ? `Approve & stock ${qty}`
                : `Approve & attach to ${selectedWo?.id || 'WO'}`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DraftInventory;
